import type { FocusBandName } from '@/sim/simulation';
import type { BandReading } from '@/sim/mortals/band';
import type { BandActivity } from '@/sim/mortals/types';
import type { Practices } from '@/sim/culture';

/**
 * THE SAME SUBJECT, AT DIFFERENT SCALES.
 *
 * A person is not one thing. She is an individual, somebody's child, one of a
 * hunting party, a member of a band, a carrier of a people's practices, and a
 * pressure on a valley's game — **all at once**, and which of those truths is
 * currently actionable is a question about where the player's attention is,
 * not about which mode they have opened. The design rule is that there is no
 * Character Mode and no Civilization Mode: you pull outward from something you
 * already care about and discover what larger processes it belongs to.
 *
 * The mechanism for that was already in the build and nobody had read it as
 * one. `FOCUS_BANDS` (`sim/simulation.ts`) is a five-rung ladder of attention
 * — presence · local · grove · valley · watershed — the wheel already walks it,
 * `stepFocus` already snaps across the deliberate gap between valley and
 * watershed, and the HUD already prints the rung's name. It was a *cast radius*
 * and nothing else. It is also, unchanged, exactly the semantic ladder: one
 * body, one encounter, one community, one landscape.
 *
 * So this file adds no ladder. It reads the one that exists and says what the
 * chosen subject IS on each rung.
 *
 * ## What must not happen here
 *
 * **No numbers.** Same wall as `ui/lives.ts`, for the same reason: the moment a
 * band is a row of quantities the player stops watching people and starts
 * auditing labour. `BandReading` arrives in simulation units and leaves as
 * *they are hungry*. `cardHTML` withholds any line about a person carrying a
 * digit anyway; this is the discipline that means the wall is never hit.
 *
 * **No invented causes.** Every sentence below is licensed by a field the
 * simulation actually computed. Where the reading cannot say why, this says
 * nothing rather than guessing — an honest silence beats a plausible sentence,
 * because a plausible sentence is unfalsifiable and the player will believe it.
 *
 * DOM-free, three-free, sim-write-free.
 */

/**
 * The four registers of attention, from the five focus bands.
 *
 * `presence` and `local` fold together: at 5–100 m the subject is a body and
 * there is nothing else the scale could mean. The other three are one each,
 * which is what the bands were tuned to be — *one tree, one clearing, one
 * grove, one valley, one watershed*.
 */
export type Register = 'intimate' | 'encounter' | 'community' | 'landscape';

export const REGISTER_OF: Readonly<Record<FocusBandName, Register>> = {
  presence: 'intimate',
  local: 'intimate',
  grove: 'encounter',
  valley: 'community',
  watershed: 'landscape',
};

/**
 * What the register is called on screen. Deliberately not the focus band's own
 * name: `grove` is a size and *the others out with them* is a subject, and the
 * card is naming a subject.
 */
export const REGISTER_WORD: Readonly<Record<Register, string>> = {
  intimate: 'them',
  encounter: 'the work they are in',
  community: 'the band they belong to',
  landscape: 'the country they live in',
};

/** What the work is, in the world's voice. The same table `ui/lives.ts` uses
 *  for a person, said about a group instead of about a pair of hands. */
const DOING: Readonly<Record<BandActivity, string>> = {
  camp: 'keeping the camp',
  idle: 'about the camp with nothing pressing',
  rest: 'resting',
  build: 'raising something',
  forage: 'out foraging',
  hunt: 'out after the herds',
  gather_wood: 'cutting wood',
  seek_water: 'gone for water',
  explore: 'away scouting',
  investigate: 'gone to where the sign pointed',
  venerate: 'gone back to the standing stone',
  travel: 'on the move',
  flee: 'running from something',
};

export const doingWord = (a: BandActivity): string => DOING[a] ?? 'about the camp';

/**
 * WHY, in the world's voice — the whole point of the read model.
 *
 * `BandSystem.reading` hands over the scored table, the felt needs, and what
 * the band can see. A card that printed that would be a debug panel; what a
 * player needs is the one or two clauses a neighbour would actually say. The
 * shape is always *this is what they are short of* + *this is what made this
 * the answer* + *this is what they nearly did instead*, and any of the three
 * may honestly be missing.
 *
 * Order is fixed and it is the order the reasons OUTRANK each other in the
 * simulation, not the order they read best in: an override first, because it
 * means the table was never consulted; then need, because `spareAttention`
 * scales every non-survival row by what is left of it — you cannot preach
 * somebody out of hunger; then opportunity.
 */
export function whyThey(r: BandReading): string[] {
  const out: string[] = [];

  // 1. the two decisions that never reached the table at all
  if (r.override === 'fire') {
    out.push('there is fire near enough that nothing else is being weighed');
    return out;
  }
  if (r.override === 'migrating') {
    out.push('they are walking to somewhere they remember as better than here');
    return out;
  }

  // 2. what they are short of. WORRY is 0.35 — three days of food in hand —
  //    and it is where the simulation itself says need begins, so it is where
  //    the sentence begins too.
  const p = r.pressures;
  const need: Array<[number, string]> = [
    [p.water, p.water > 0.7 ? 'they are badly short of water' : 'their water is running low'],
    [p.food, p.food > 0.7 ? 'they are hungry' : 'their stores are thinning'],
    [p.shelter, 'some of them are sleeping in the open'],
    [p.safety, p.safety > 0.6 ? 'this ground frightens them' : 'they are uneasy here'],
  ];
  const worst = need.filter(([v]) => v > 0.35).sort((a, b) => b[0] - a[0])[0];
  if (worst) out.push(worst[1]);
  else if (p.food < 0.12 && p.water < 0.12) out.push('they want for nothing today');

  // 3. what made THIS the answer. Only the winning row's own strongest input
  //    is named — a card that listed every term would be the score table with
  //    the numbers spelled out.
  const because = opportunity(r);
  if (because) out.push(because);

  // 4. and what they nearly did instead, which is the most legible fact about
  //    any choice and costs nothing to say
  if (r.runnerUp && r.runnerUp !== r.chose) {
    out.push(`they came close to ${DOING[r.runnerUp] ?? 'something else'} instead`);
  }
  // 5. …and the sign on their minds, while they have not gone to look. An
  //    Omen is answered on day 27–37 on the shipped century, which is outside
  //    a session, and until now nothing between the cast and the trip said
  //    the band had noticed at all. `drawn` is the anomaly's own pull —
  //    salience × what signs have earned in this band's experience — so a
  //    band that has stopped believing in signs says nothing here, honestly.
  //    Law 10 as everywhere: a thing they cannot explain, never a hand.
  if (r.chose !== 'investigate' && r.drawn > 0.15) {
    out.push(r.drawn > 0.4
      ? 'a sign they cannot explain is pulling at them, and they have not gone to look'
      : 'they have seen something they cannot explain, and have not gone to look');
  }
  // 6. and whether they will still be one band tomorrow. `frayedDays` climbs
  //    only while cohesion sits under the fission line (0.36) and the split
  //    fires at 45 of them, so *coming apart* is a true forecast with weeks
  //    of warning — the purest check-tomorrow fact the sim computes, spoken
  //    at last. The softer line reads plain low cohesion: the shipped
  //    century's bands sit 0.36–0.72, so *loosely* names the bottom of that
  //    range and nobody else (measured 2026-08-23, one band at 0.36).
  if (r.frayedDays > 10) {
    out.push('they are coming apart, and have been for weeks');
  } else if (r.frayedDays > 0) {
    out.push('they are coming apart');
  } else if (r.cohesion <= 0.42) {
    out.push('they hold together loosely these days');
  }
  return out;
}

/** The one input that carried the winning row, where the reading knows one. */
function opportunity(r: BandReading): string | undefined {
  switch (r.chose) {
    case 'hunt':
      return r.abundance > 0.6 ? 'there is game within reach of a day\'s walk'
        : r.abundance > 0.3 ? 'there is game about, though not much' : undefined;
    case 'forage':
      return r.forage > 0.55 ? 'this ground gives up food easily'
        : r.forage < 0.25 ? 'they are working thin ground for it' : undefined;
    case 'gather_wood':
      return r.woodNeed > 0.8 ? 'something they are building wants more wood than they hold'
        : undefined;
    case 'investigate':
      // The only row on the table a god can have put there. Never says whose
      // hand it was — law 10 — only that something is unexplained.
      return r.drawn > 0.4 ? 'something none of them can explain is pulling at them'
        : 'something happened here that nobody has an answer for';
    case 'venerate':
      return (r.regard ?? 0) > 0.4 ? 'that ground has come to matter to them'
        : 'they are curious about a place, not yet devoted to it';
    case 'explore':
      return r.pressures.uncertainty > 0.5 ? 'they do not know this country well'
        : 'they are fed enough to be restless';
    case 'build':
      return 'they are settled enough to raise something';
    case 'rest':
      return 'they are not holding together well';
    default:
      return undefined;
  }
}

/**
 * WHAT A GOD COULD REINFORCE HERE, at this register.
 *
 * The contextual half of Strengthen, and the rule is law 21's: *you can only
 * strengthen what is already being expressed.* At the intimate register that
 * is the person's own axes and `ui/lives.ts` already names them. Wider, the
 * expression is not theirs — it is the group's, and what is being expressed is
 * **the work**. So this returns the process, not a list of traits, because a
 * wide Strengthen is a different intervention and not the close one with a
 * bigger radius.
 *
 * Empty means *nothing here is expressed enough to lean on*, which the card
 * says out loud. That sentence is the design being taught.
 */
export function reinforceable(
  register: Register, r: BandReading | null,
  /** The errand of the group actually in front of the player, when that is a
   *  narrower thing than the band's own decision. */
  here?: BandActivity,
): string[] {
  if (!r || register === 'intimate') return [];
  if (r.override === 'fire') return [];
  if (register === 'encounter') return [HERE[here ?? r.chose] ?? 'the work they are at'];
  // The COMMUNITY leans on the band's standing TENDENCY rather than today's
  // errand — `BAND_TRACK` is what carries a roster's character into the band's
  // own, and a tendency is what a wide cast can honestly reach. Two offers,
  // and they are genuinely different interventions: the thing itself, and
  // their appetite for going on doing it.
  if (register === 'community') {
    return [BAND_OF[r.chose] ?? 'what this band keeps doing',
            KEEP_AT[r.chose] ?? 'their persistence'];
  }
  // …and the LANDSCAPE names no band at all, because a watershed cast is six
  // kilometres wide and this band is one of several standing in it. Offering
  // *this band's hunting* out here would promise a precision the cast is not
  // going to honour — law 14, and `PRECISION × SCALE × INTENSITY` refusing to
  // let the card oversell the widest gesture in the game.
  return [KEEP_AT[r.chose] ?? 'their persistence'].map(regional);
}

/** The same tendency, said about a country rather than about a band. */
const regional = (s: string): string =>
  s.startsWith('their ') ? `${s.slice(6)}, wherever it is felt here` : s;

/**
 * The three phrasings of one activity, and they are three because English
 * makes them three. Built as tables rather than as interpolation because the
 * first version interpolated and produced *their willingness to keep at out
 * after the herds*, which is what a card says when a sentence is assembled out
 * of a phrase that was only ever meant to be a predicate.
 */
/** The group in front of you, doing it: *the hunt they are on*. */
const HERE: Partial<Record<BandActivity, string>> = {
  hunt: 'the hunt they are on', forage: 'the foraging they are at',
  gather_wood: 'the wood they are cutting', build: 'the work they are raising',
  explore: 'the ground they are scouting', seek_water: 'their search for water',
  investigate: 'the search they are on', venerate: 'the journey they are making',
  travel: 'the walk they are on', rest: 'the rest they are taking',
  idle: 'the quiet they are keeping', camp: 'the camp they are keeping',
  flee: 'their flight',
};
/** The band's standing version of it: *this band's hunt*. */
const BAND_OF: Partial<Record<BandActivity, string>> = {
  hunt: 'this band\'s hunting', forage: 'this band\'s foraging',
  gather_wood: 'this band\'s wood-cutting', build: 'what this band is raising',
  explore: 'this band\'s wandering', seek_water: 'this band\'s search for water',
  investigate: 'this band\'s need for an answer', venerate: 'this band\'s pilgrimage',
  travel: 'this band\'s migration', rest: 'this band\'s rest',
  idle: 'this band\'s ease', camp: 'this camp itself', flee: 'their flight',
};
/** …and their appetite for going on doing it: *their appetite for the chase*. */
const KEEP_AT: Partial<Record<BandActivity, string>> = {
  hunt: 'their appetite for the chase', forage: 'their patience for gathering',
  gather_wood: 'their will to keep cutting', build: 'their will to finish it',
  explore: 'their restlessness', seek_water: 'their determination to find it',
  investigate: 'their need to know', venerate: 'their devotion to that place',
  travel: 'their willingness to keep walking', rest: 'their need to recover',
  idle: 'their contentment here', camp: 'their attachment to this ground',
  flee: 'their will to run',
};

/**
 * WHAT A PEOPLE IS SEEN TO DO — the landscape register's own content.
 *
 * `sim/culture.ts` has simulated six practices since §50 and **five of them
 * have never had a reader**: language, food, burial, story and symbol drift,
 * are inherited whole by daughter peoples, and are snapshotted every
 * world-year, and only the roof shape was ever visible. They are the most
 * legible thing culture produces — *these people build round and lay their
 * dead in the earth* is a fact you can stand in a valley and check, which is
 * exactly what a leaning of 0.62 is not.
 *
 * Two at a time, deterministically chosen by the people's own name, so one
 * people always reads the same way and two neighbours are described in the
 * same terms — which is what makes a difference between them noticeable at
 * all. All six on one card would be a census.
 */
const PRACTICE_SAID: {
  [K in keyof Practices]: (v: Practices[K]) => string;
} = {
  language: (v) => `their speech is ${v}`,
  food: (v) => `they live as ${v}`,
  building: (v) => (v === 'round' ? 'they build round'
    : v === 'longhouse' ? 'they build long'
      : v === 'sunken' ? 'they dig their floors down' : 'they raise lean-tos'),
  burial: (v) => (v === 'in earth' ? 'they lay their dead in the earth'
    : v === 'to fire' ? 'they give their dead to fire'
      : v === 'left high' ? 'they leave their dead on high ground'
        : 'they lay their dead under stones'),
  story: (v) => `everyone here knows the story of ${v}`,
  symbol: (v) => `they cut the ${v} into posts and skin`,
};

const PRACTICE_ORDER = ['burial', 'building', 'food', 'symbol', 'story', 'language'] as const;

export function peopleNow(
  name: string, practices: Practices, seed: number, keep = 2,
): string[] {
  const out = [`the ${name}`];
  // rotate the window by the people's own hash so different peoples lead with
  // different practices, and the same people always leads with the same one
  const start = Math.abs(seed) % PRACTICE_ORDER.length;
  for (let i = 0; i < PRACTICE_ORDER.length && out.length <= keep; i++) {
    const k = PRACTICE_ORDER[(start + i) % PRACTICE_ORDER.length];
    const say = PRACTICE_SAID[k] as (v: string) => string;
    out.push(say(practices[k]));
  }
  return out;
}

/**
 * HOW UNLIKE THE NEIGHBOURS THEY HAVE BECOME.
 *
 * `CultureSystem.practiceDistance` is the fraction of the six practices two
 * peoples visibly differ on, and it has had no reader either. It is the half
 * of divergence a person standing in the next valley would actually notice —
 * which is why it exists separately from `distance()`, and why it is the one
 * worth saying.
 */
export function apartFrom(other: string, practiceDistance: number): string | undefined {
  if (practiceDistance <= 0) return `they and the ${other} still live the same way`;
  if (practiceDistance < 0.34) return `they and the ${other} still live much alike`;
  if (practiceDistance < 0.67) return `they and the ${other} have grown apart`;
  return `they and the ${other} have almost nothing left in common`;
}

/**
 * A HERD, AT ANY REGISTER — `cellView` and `rangeAt`, which have had no `src/`
 * caller since they were written. The card could say a species and a head
 * count and nothing else: not whether the herd is growing or dying back, not
 * whether the ground under it is stripped, not how long a species has been
 * gone from a valley. All three are one `Map.get` away.
 */
export function herdTrend(
  view: { trend: 'growing' | 'declining' | 'steady'; range: number },
  species: string,
): string[] {
  const out: string[] = [];
  if (view.trend === 'growing') out.push('more of them are gathering here');
  else if (view.trend === 'declining') out.push('there are more here than the ground can carry');
  // `range` is 1 where pristine. Under about four-fifths the wear is a thing
  // you can see from the air, and `populations.stirring` already words its own
  // version of this as *stripped*.
  if (view.range < 0.55) out.push('they have grazed this ground bare');
  else if (view.range < 0.8) out.push('the ground under them is wearing thin');
  if (!out.length) out.push(`the ${species} are steady on this ground`);
  return out;
}
